import React, { memo } from 'react';
import { createPortal } from 'react-dom';
import type {
  MentionTooltipProps,
  MentionTooltipDisplayMode,
  MentionTooltipUser
} from './MentionTooltip.types';
import './MentionTooltip.css';
import '../../CommentText/CommentText.css';

// Helper function to render user based on display mode
const renderUserDisplay = (
  user: MentionTooltipUser,
  displayMode: MentionTooltipDisplayMode
) => {
  // If custom function provided, use it
  if (typeof displayMode === 'function') {
    return displayMode(user);
  }

  // Built-in display modes
  switch (displayMode) {
    case 'name':
      return <div className="tooltip-email">{user.name}</div>;

    case 'username':
      return <div className="tooltip-email">@{user.username}</div>;

    case 'email':
      return <div className="tooltip-email">{user.email}</div>;

    case 'both':
      return (
        <>
          <div className="tooltip-username">{user.name}</div>
          <div className="tooltip-email">{user.email}</div>
        </>
      );

    default:
      return <div className="tooltip-email">{user.name}</div>;
  }
};

const MentionTooltipComponent: React.FC<MentionTooltipProps> = ({
  show,
  position,
  user,
  hyperlink,
  loading,
  type,
  style,
  displayMode = 'name' // Default to showing only name
}) => {
  if (!show) {
    return null;
  }

  const tooltipClassName = `comment-text__mention-tooltip ${
    position.showAbove !== false
      ? 'comment-text__mention-tooltip--above'
      : 'comment-text__mention-tooltip--below'
  }`;

  const tooltipContent = (
    <div
      className={tooltipClassName}
      data-type={type}
      style={{
        left: position.x,
        top: position.y,
        position: 'fixed', // Use fixed positioning for portal
        zIndex: 2147483547, // Ensure tooltip appears above other elements
        ...style
      }}
    >
      {loading ? (
        <div style={{ padding: '8px 12px' }}>Loading...</div>
      ) : type === 'mention' && user ? (
        renderUserDisplay(user, displayMode)
      ) : type === 'hyperlink' && hyperlink ? (
        <>
          <div className="tooltip-username">{hyperlink.domain}</div>
          <div className="tooltip-email">{hyperlink.url}</div>
        </>
      ) : null}
    </div>
  );

  // Use portal to render tooltip in document body
  return createPortal(tooltipContent, document.body);
};

export const MentionTooltip = memo(MentionTooltipComponent);
